import { Injectable } from '@angular/core';
import { Logger } from '@crt/sui';

export interface NotificationOptions {
  body: string;
  icon?: string;
  tag?: string;
  title: string;
}

@Injectable({
  providedIn: 'root',
})
export class BrowserNotificationService {
  constructor() {
    this.requestNotificationPermission();
  }

  /**
   * Check if browser supports notifications
   */
  isSupported(): boolean {
    return typeof window !== 'undefined' && 'Notification' in window;
  }

  /**
   * Get current notification permission status
   */
  getPermission(): NotificationPermission {
    if (!this.isSupported()) {
      return 'denied';
    }
    return Notification.permission;
  }

  /**
   * Request permission for browser to show desktop notifications.
   */
  async requestNotificationPermission(): Promise<void> {
    if (!this.isSupported()) {
      Logger.debug('Browser does not support notifications');
      return;
    }

    if (Notification.permission === 'granted') {
      return;
    }

    if (Notification.permission !== 'denied') {
      try {
        await Notification.requestPermission();
      } catch (error) {
        Logger.error('Error requesting notification permission:', { data: { error } });
      }
    } else {
      Logger.debug('Notification permission denied');
    }
  }

  /**
   * Ensure notification permission is granted, requesting if necessary.
   * @returns The permission status after attempting to request
   */
  private async ensurePermission(): Promise<NotificationPermission> {
    if (!this.isSupported()) {
      return 'denied';
    }

    let permission = Notification.permission;

    if (permission === 'granted') {
      return permission;
    }

    if (permission === 'default') {
      try {
        permission = await Notification.requestPermission();
      } catch (error) {
        Logger.error('Error requesting notification permission:', { data: { error } });
        return 'denied';
      }
    }

    if (permission === 'denied') {
      Logger.debug('Notification permission denied');
    }

    return permission;
  }

  /**
   * Show a desktop notification.
   * @param options - Notification options including title, body, and optional settings
   * @returns Promise that resolves when notification is shown or rejected
   */
  async showNotification(options: NotificationOptions): Promise<void> {
    if (!this.isSupported()) {
      Logger.debug('Browser does not support notifications');
      return;
    }

    const permission = await this.ensurePermission();

    if (permission !== 'granted') {
      Logger.debug('Notification permission not granted:', { data: { permission } });
      return;
    }

    try {
      const notification = new Notification(options.title, {
        body: options.body,
        icon: options.icon ?? '/favicon.ico',
        tag: options.tag ?? 'default',
      });

      // Focus window when notification is clicked
      notification.onclick = (): void => {
        window.focus();
        notification.close();
      };
    } catch (error) {
      Logger.error('Error showing desktop notification:', { data: { error } });
    }
  }
}
